import { useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { usePermessi } from "@/shared/auth/permessi";
import { decidiRegola, nuovaRegola, regoleAgente, type Regola, type StatoRegola } from "../agente";

/** I passaggi permessi, uguali a ``connettore/regole.py``: il backend resta la difesa vera. */
const AZIONI: Record<StatoRegola, { stato: StatoRegola; etichetta: string }[]> = {
  proposta: [
    { stato: "approvata", etichetta: "Approva" },
    { stato: "rifiutata", etichetta: "Rifiuta" },
  ],
  approvata: [{ stato: "spenta", etichetta: "Spegni" }],
  spenta: [{ stato: "approvata", etichetta: "Riaccendi" }],
  rifiutata: [{ stato: "proposta", etichetta: "Riproponi" }],
};

const TITOLI: Record<StatoRegola, string> = {
  proposta: "Da decidere",
  approvata: "Attive",
  spenta: "Spente",
  rifiutata: "Rifiutate",
};

const ORDINE: StatoRegola[] = ["proposta", "approvata", "spenta", "rifiutata"];

/**
 * Regole di Kery per le risposte di Claude (piano agente, tappa 6). Claude ne
 * propone al massimo tre a settimana guardando le bozze ritoccate; nel prompt
 * entrano solo le «Attive». Approvare, spegnere e scriverne una nuova lo puo'
 * fare solo Davide (ruolo god): gli altri le leggono.
 */
export function RegoleAgente() {
  const { isGod } = usePermessi();
  const [regole, setRegole] = useState<Regola[] | null>(null);
  const [testo, setTesto] = useState("");
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    regoleAgente()
      .then((r) => vivo && setRegole(r))
      .catch((err) => vivo && setErrore(prettifyApiError(err)));
    return () => {
      vivo = false;
    };
  }, []);

  async function esegui(azione: () => Promise<Regola>) {
    setInCorso(true);
    setErrore(null);
    try {
      const nuova = await azione();
      setRegole((prima) => [nuova, ...(prima ?? []).filter((r) => r.id !== nuova.id)]);
      return true;
    } catch (err) {
      setErrore(prettifyApiError(err));
      return false;
    } finally {
      setInCorso(false);
    }
  }

  if (regole === null) {
    return errore ? (
      <p role="alert" className="mt-2 rounded-xl bg-red-soft px-3 py-2 text-[12.5px]">
        {errore}
      </p>
    ) : (
      <p className="mt-2 text-[12px]">Carico le regole…</p>
    );
  }

  return (
    <div
      className="mt-2 rounded-xl border border-line-2 bg-card px-3 py-2"
      aria-label="Regole di Kery"
    >
      {regole.length === 0 && <p className="text-[12px]">Ancora nessuna regola.</p>}
      {ORDINE.map((stato) => {
        const gruppo = regole.filter((r) => r.stato === stato);
        if (gruppo.length === 0) return null;
        return (
          <div key={stato} className="mt-1.5">
            <p className="text-[12px] font-semibold">
              {TITOLI[stato]} ({gruppo.length})
            </p>
            <ul className="mt-1 space-y-1.5">
              {gruppo.map((regola) => (
                <li key={regola.id} data-testid={`regola-${regola.id}`}>
                  <p className="whitespace-pre-wrap">{regola.testo}</p>
                  {regola.motivo && (
                    <p className="text-[11.5px]">
                      Perché: {regola.motivo}
                      {regola.proposta_da === "claude" ? " (proposta da Claude)" : ""}
                    </p>
                  )}
                  {isGod && (
                    <div className="mt-1 flex gap-1.5">
                      {AZIONI[regola.stato].map((azione) => (
                        <button
                          key={azione.stato}
                          type="button"
                          disabled={inCorso}
                          onClick={() => void esegui(() => decidiRegola(regola.id, azione.stato))}
                          className="h-7 rounded-full border border-line-2 px-3 text-[12px] font-semibold hover:bg-paper disabled:opacity-45"
                        >
                          {azione.etichetta}
                        </button>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      {errore && (
        <p role="alert" className="mt-2 rounded-xl bg-red-soft px-3 py-2 text-[12.5px]">
          {errore}
        </p>
      )}

      {isGod ? (
        <form
          className="mt-2 flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const pulito = testo.trim();
            if (!pulito) return;
            void esegui(() => nuovaRegola(pulito)).then((ok) => ok && setTesto(""));
          }}
        >
          <input
            value={testo}
            onChange={(event) => setTesto(event.target.value)}
            maxLength={400}
            disabled={inCorso}
            placeholder="Nuova regola, es.: dai sempre del lei"
            aria-label="Nuova regola"
            className="h-8 min-w-[180px] flex-1 rounded-full border border-line-2 bg-paper px-3 text-[12.5px] outline-none focus:border-ink"
          />
          <button
            type="submit"
            disabled={inCorso || !testo.trim()}
            className="h-8 rounded-full border border-ink px-3 text-[12px] font-semibold disabled:opacity-45"
          >
            Aggiungi
          </button>
        </form>
      ) : (
        <p className="mt-1.5 text-[11.5px]">Le regole le decide Davide.</p>
      )}
    </div>
  );
}
